import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ToggleLeft } from "lucide-react";
import { getDisabledModifiersSummaryAPI } from "../../services/api";
import { outOfStockFor } from "./OutOfStockModifiers";

interface DisabledItem {
  presetId: string;
  presetName: string;
  option: string;
  label: string;
  /** "everywhere" = off in the set itself; "location" = off only at this location */
  scope: "everywhere" | "location";
  since?: string | null;
  sinceEstimated?: boolean;
}

interface LocationSummary {
  hotelId: string;
  hotelName: string;
  count: number;
  items: DisabledItem[];
}

const ItemList: React.FC<{ items: DisabledItem[] }> = ({ items }) =>
  items.length === 0 ? (
    <p className="m-0 px-4 py-3 text-sm text-gray-500">All modifier choices are available.</p>
  ) : (
    <ul className="m-0 list-none divide-y p-0">
      {items.map((item, i) => (
        <li key={i} className="flex items-center justify-between gap-3 px-4 py-2 text-sm">
          <span className="min-w-0">
            <span className="font-medium text-gray-800">{item.label}</span>
            <span className="text-gray-500"> · {item.presetName}</span>
            {item.since && (
              <span className="text-gray-400">
                {" "}
                · {item.sinceEstimated ? "≈ " : ""}
                {outOfStockFor(item.since)}
              </span>
            )}
          </span>
          <span
            className={`shrink-0 rounded px-2 py-0.5 text-[11px] ${
              item.scope === "everywhere"
                ? "bg-red-100 text-red-700"
                : "bg-amber-100 text-amber-800"
            }`}
          >
            {item.scope === "everywhere" ? "Off everywhere" : "Off at this location"}
          </span>
        </li>
      ))}
    </ul>
  );

/**
 * Disabled modifier choices. Location admin: their location's list.
 * Master: count per location (expandable), or one location's list when the
 * dashboard is filtered to it.
 */
const DisabledModifiersCard: React.FC<{ selectedLocation: string; isMasterAdmin: boolean }> = ({
  selectedLocation,
  isMasterAdmin,
}) => {
  const [locations, setLocations] = useState<LocationSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    getDisabledModifiersSummaryAPI()
      .then((res) => setLocations(res.data?.data || []))
      .catch(() => setLocations([]))
      .finally(() => setLoading(false));
  }, []);

  const single = !isMasterAdmin
    ? locations[0]
    : selectedLocation !== "all"
      ? locations.find((l) => l.hotelId === selectedLocation)
      : undefined;
  const shown = single ? [single] : locations;
  const total = shown.reduce((sum, l) => sum + l.count, 0);

  return (
    <div className="mb-6 overflow-hidden rounded-xl border border-[rgba(0,0,0,0.1)] bg-white">
      <div className="flex items-center justify-between gap-3 bg-gray-100 p-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-red-500 text-white">
            <ToggleLeft size={20} />
          </div>
          <div>
            <h2 className="m-0 text-base font-bold text-gray-700 sm:text-lg">
              Out of Stock Modifiers{single ? ` · ${single.hotelName}` : ""}
            </h2>
            <span className="text-xs text-gray-500">
              Modifier choices customers can't pick right now
            </span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xl font-bold text-red-600">{loading ? "…" : total}</div>
          <Link to="/admin/out-of-stock-modifiers" className="text-xs text-blue-600">
            View all
          </Link>
        </div>
      </div>

      {loading ? (
        <p className="m-0 px-4 py-3 text-sm text-gray-500">Loading…</p>
      ) : single ? (
        <ItemList items={single.items} />
      ) : (
        <ul className="m-0 list-none divide-y p-0">
          {locations.map((loc) => (
            <li key={loc.hotelId}>
              <button
                type="button"
                onClick={() => setExpanded(expanded === loc.hotelId ? null : loc.hotelId)}
                className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-sm hover:bg-gray-50"
              >
                <span className="font-medium text-gray-800">{loc.hotelName}</span>
                <span className="flex items-center gap-2">
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                      loc.count ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"
                    }`}
                  >
                    {loc.count} disabled
                  </span>
                  <ChevronDown
                    size={16}
                    className={`text-gray-500 transition-transform ${
                      expanded === loc.hotelId ? "rotate-180" : ""
                    }`}
                  />
                </span>
              </button>
              {expanded === loc.hotelId && (
                <div className="bg-gray-50">
                  <ItemList items={loc.items} />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default DisabledModifiersCard;
